import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, IconButton, Typography } from '@mui/material';
import { MdArrowBack } from 'react-icons/md';
import { useLocation, useNavigate } from 'react-router-dom';
import ProductForm from './ProductForm';
import { applyFormChange, authToken, buildProductFormData, FONT, initialFormState } from './productShared';
import { getData, postFileUpload } from '../../Webservice/webservice';

const AddProduct = () => {
    const navigate = useNavigate();
    const location = useLocation();
    // "Add as new product" in the product check passes the name that was searched for
    const [form, setForm] = useState(() => ({ ...initialFormState, title: location.state?.title || '' }));
    const [categories, setCategories] = useState([]);
    const [subcategories, setSubcategories] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [serverError, setServerError] = useState('');

    const fetchCategories = useCallback(async () => {
        try {
            const res = await getData('categories/list?limit=1000', null, authToken());
            setCategories(Array.isArray(res?.categories) ? res.categories : []);
        } catch { setCategories([]); }
    }, []);

    const fetchSubcategories = useCallback(async (categoryId) => {
        if (!categoryId) { setSubcategories([]); return; }
        try {
            const res = await getData(`subcategories/list?limit=1000&category_id=${categoryId}`, null, authToken());
            setSubcategories(Array.isArray(res?.subcategories) ? res.subcategories : []);
        } catch { setSubcategories([]); }
    }, []);

    useEffect(() => { fetchCategories(); }, [fetchCategories]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((f) => applyFormChange(f, name, value));
        if (name === 'category_id') fetchSubcategories(value);
    };

    const handleAdd = async () => {
        setSubmitting(true);
        setServerError('');
        try {
            await postFileUpload('product/add', buildProductFormData(form), authToken());
            navigate('/products', { state: { notice: `“${form.title.trim()}” was sent for approval. It goes live once our team approves it.` } });
        } catch (error) {
            setServerError(error?.message || 'Could not add the product.');
            window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Box sx={{ p: { xs: 1.5, md: 3 }, fontFamily: FONT }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <IconButton onClick={() => navigate('/products')} sx={{ background: '#f1f5f9' }}><MdArrowBack size={18} /></IconButton>
                <Box>
                    <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 24, color: '#0f172a' }}>Add a product</Typography>
                    <Typography sx={{ fontFamily: FONT, fontSize: 13.5, color: '#64748b' }}>Tell us about it once; we check it before it goes on sale</Typography>
                </Box>
            </Box>
            <Alert severity="info" sx={{ mb: 2.5 }}>
                New products are reviewed by our team first. Make sure the title is not already in the catalogue: use <b>Check product</b> on the list page to ask to supply an existing one instead.
            </Alert>
            <ProductForm
                form={form}
                onFormChange={handleChange}
                onCancel={() => navigate('/products')}
                onSubmit={handleAdd}
                categories={categories}
                subcategories={subcategories}
                fetchCategories={fetchCategories}
                fetchSubcategories={fetchSubcategories}
                submitting={submitting}
                serverError={serverError}
            />
        </Box>
    );
};

export default AddProduct;
